'use client'

import { useState } from 'react'
import { classNames, legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { contract, css } from '@/deskUiSkeletons/editor/Editor.helpers'
import { EditorBodyFields } from '@/deskUiSkeletons/editor/EditorBodyFields'
import { EditorHeadFields, HeadShort } from '@/deskUiSkeletons/editor/components/Head/Head'
import { EditorConfigureModal } from '@/deskUiSkeletons/editor/components/Modal/ConfigureModal'
import { EditorAttachmentsModal, editorAttachmentCount } from '@/deskUiSkeletons/editor/components/Modal/AttachmentsModal'
import { EditorAttachmentsStrip } from '@/deskUiSkeletons/editor/components/Attachments/AttachmentsStrip'
import { useEditorAttachments } from '@/deskUiSkeletons/editor/useEditorAttachments'
import { EditorFormDefaults, HiddenEditorFormFields, useEditorTemplateContract } from '@/deskUiSkeletons/editor/EditorTemplateContract'
import { EditorActions } from './components/Actions/Actions'

// Std editor shell — modern column layout. Configure and Attachments live in
// the shared modals (React-local open state; legacy's inline toggle-settings
// swap is retired). The editor.settings contract stays mounted-empty and
// force-hidden, and both modals render INSIDE editor.root so defaults
// hydration and legacy input binding keep resolving (mailbox recipe).
export function EditorTemplate({ editorMode }: { editorMode?: EditorMode }) {
  const formModel = useEditorTemplateContract(editorMode)
  const [configureOpen, setConfigureOpen] = useState(false)
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)
  const attachCount = editorAttachmentCount(useEditorAttachments(), editorMode)

  return (
    // EDITOR-PAGE-PADDING: the editor reads as a document page — a centered,
    // padded sheet instead of a card flush against the workspace edges.
    <div className="md-editor-page mx-auto w-full max-w-[980px] px-3 py-4 sm:px-6 sm:py-6 md:py-8">
    <div {...legacyHookAttrs('editor.root', 'md-editor flex min-h-[560px] flex-col border border-[#d9e1e8] bg-white text-sm text-[#17212b] font-[-apple-system,BlinkMacSystemFont,Segoe_UI,Arial,sans-serif] shadow-[0_10px_30px_rgba(15,23,42,0.08)] sm:rounded-lg')}>
      <EditorFormDefaults {...formModel} editorMode={editorMode} />
      <header className="md-editor__header flex h-12 flex-[0_0_48px] items-center gap-3 border-b border-[#e2e8ee] px-4">
        <h1 className="md-editor__title min-w-0 flex-1 truncate text-[15px] font-semibold">
          {editorMode === 'update_msg' ? 'Edit message' : editorMode === 'new_answer' ? 'New answer' : 'New message'}
        </h1>
        <div
          {...contract('editor.close')}
          className="md-editor__close flex h-9 w-9 flex-none cursor-pointer items-center justify-center rounded text-[20px] leading-none text-[#526272] hover:bg-[#edf2f6]"
          role="button"
          tabIndex={0}
          title="Close editor"
          aria-label="Close editor"
        >
          ×
        </div>
      </header>
      <HeadShort />
      <form {...contract('editor.form')} className={classNames('md-editor__main flex min-h-0 flex-1 flex-col', css('editor.formHead'))}>
        <EditorHeadFields editorMode={editorMode} />
        <EditorBodyFields />
        <div className="md-editor__strip-row px-4 pb-3 empty:hidden">
          <EditorAttachmentsStrip editorMode={editorMode} />
        </div>
        <HiddenEditorFormFields inputs={formModel.inputs} />
      </form>
      <div {...contract('editor.settings')} className="!hidden" hidden aria-hidden="true" />
      <EditorActions
        attachCount={attachCount}
        onOpenAttachments={() => setAttachmentsOpen(true)}
        onOpenConfigure={() => setConfigureOpen(true)}
      />
      <EditorConfigureModal open={configureOpen} onClose={() => setConfigureOpen(false)} />
      <EditorAttachmentsModal open={attachmentsOpen} onClose={() => setAttachmentsOpen(false)} editorMode={editorMode} />
    </div>
    </div>
  )
}
